import { v4 as uuid } from 'uuid'
import {
    Broker_OrderType,
    Broker_Variety,
    Order,
    OrderType,
    Product,
    TransactionType,
    Validity,
} from '../proto/generated/order'
import { TemplateOrderExecutionParameters } from '../proto/generated/quick-trade'
import { Security } from '../proto/generated/security'
import {
    ConditionalEntryState,
    hasConditionalRuleContent,
    orderExecutionParamsForConditionalEntry,
} from './conditionalEntry'
import { buildQuickTradeConditionalPlacePlan } from './quickTradeConditionalPlace'

export type ApplyTemplateConditionalParams = {
    templateParams: TemplateOrderExecutionParameters
    mainSecurity: Security
    batchId: string
    basketId?: string
    strategyGroup?: string
    /** Single-leg: primary order index (default 0) */
    primaryIndex?: number
    /** Single-leg CX: same qty and tx as primary */
    primaryQuantity?: number
    primaryTransactionType?: TransactionType
    primaryProduct?: Product
    ceClientOrderId?: string
    cxClientOrderId?: string
}

/** UI + place: treat stored condition as enabled even if legacy templates omit the bool flag. */
export function conditionalEntryStateFromTemplate(
    p: TemplateOrderExecutionParameters | undefined
): ConditionalEntryState {
    const condition = p?.conditionalEntryCondition
    return {
        enabled: !!p?.addConditionalEntry || hasConditionalRuleContent(condition),
        condition,
    }
}

export function conditionalExitStateFromTemplate(
    p: TemplateOrderExecutionParameters | undefined
): ConditionalEntryState {
    const condition = p?.conditionalExitCondition
    return {
        enabled: !!p?.addConditionalExit || hasConditionalRuleContent(condition),
        condition,
    }
}

/** Single-leg: merge CE onto primary; append CX sibling. */
export function applyTemplateConditionalToSingleLegOrders(
    orders: Order[],
    params: ApplyTemplateConditionalParams
): void {
    const p = params.templateParams
    if (p.transactionType == null) return

    const idx = params.primaryIndex ?? 0
    const primary = orders[idx]
    if (!primary) return

    const entryState = conditionalEntryStateFromTemplate(p)
    const exitState = conditionalExitStateFromTemplate(p)

    const entryPlan = buildQuickTradeConditionalPlacePlan({
        placeType: 'trade',
        isSquareOffOrder: false,
        mode: 'regular',
        orderType: OrderType.NORMAL,
        modifyingOrderType: undefined,
        conditionalState: entryState,
        baseExecutionParams: undefined,
        useSeparateConditionalEntryLeg: false,
    })

    if (
        entryState.enabled &&
        hasConditionalRuleContent(entryPlan.entryParams?.condition)
    ) {
        primary.orderType = entryPlan.mainOrderType
        if (entryPlan.mainExecutionParams) {
            primary.orderExecutionParams = entryPlan.mainExecutionParams
        }
    }

    const exitParamsPreview = orderExecutionParamsForConditionalEntry(undefined, exitState, {
        stripWhenDisabled: true,
    })
    if (exitState.enabled && hasConditionalRuleContent(exitParamsPreview?.condition)) {
        const exitParams = exitParamsPreview
        const tx = params.primaryTransactionType ?? primary.transactionType ?? p.transactionType
        orders.push({
            security: {
                code: params.mainSecurity.code ?? '',
                exchange: params.mainSecurity.exchange ?? 0,
            },
            transactionType: tx,
            orderType: OrderType.CONDITIONAL_EXIT,
            brokerOrderType: Broker_OrderType.MARKET_ORDER,
            quantity: params.primaryQuantity ?? primary.quantity ?? 0,
            isBasket: false,
            batchId: params.batchId,
            orderDate: Date.now(),
            product: params.primaryProduct ?? primary.product ?? Product.CNC,
            price: 0,
            triggerPrice: 0,
            brokerVariety: Broker_Variety.REGULAR,
            validity: Validity.DAY,
            clientOrderId: params.cxClientOrderId ?? uuid(),
            orderExecutionParams: exitParams,
            immediateExecutePaperOrder: false,
            ...(params.strategyGroup ? { strategyGroup: params.strategyGroup } : {}),
        })
    }
}

/** Basket/strategy: append separate CE and CX aux legs on underlying. */
export function appendTemplateConditionalLegs(
    orders: Order[],
    params: ApplyTemplateConditionalParams
): void {
    const p = params.templateParams
    if (p.transactionType != null || !params.basketId) return

    const entryState = conditionalEntryStateFromTemplate(p)
    const exitState = conditionalExitStateFromTemplate(p)

    const entryPlan = buildQuickTradeConditionalPlacePlan({
        placeType: 'trade',
        isSquareOffOrder: false,
        mode: 'regular',
        orderType: OrderType.NORMAL,
        modifyingOrderType: undefined,
        conditionalState: entryState,
        baseExecutionParams: undefined,
        useSeparateConditionalEntryLeg: true,
    })

    const auxSecurity = {
        code: params.mainSecurity.code ?? '',
        exchange: params.mainSecurity.exchange ?? 0,
    }

    if (
        entryState.enabled &&
        entryPlan.appendConditionalEntryLeg &&
        hasConditionalRuleContent(entryPlan.entryParams?.condition)
    ) {
        orders.push({
            security: auxSecurity,
            transactionType: TransactionType.BUY,
            orderType: OrderType.CONDITIONAL_ENTRY,
            brokerOrderType: Broker_OrderType.MARKET_ORDER,
            quantity: 1,
            isBasket: true,
            basketId: params.basketId,
            batchId: params.batchId,
            orderDate: Date.now(),
            product: Product.CNC,
            price: 0,
            triggerPrice: 0,
            brokerVariety: Broker_Variety.REGULAR,
            validity: Validity.DAY,
            clientOrderId: params.ceClientOrderId ?? uuid(),
            orderExecutionParams: entryPlan.entryParams,
            immediateExecutePaperOrder: false,
            ...(params.strategyGroup ? { strategyGroup: params.strategyGroup } : {}),
        })
    }

    const exitParams = orderExecutionParamsForConditionalEntry(undefined, exitState, {
        stripWhenDisabled: true,
    })
    if (exitState.enabled && hasConditionalRuleContent(exitParams?.condition)) {
        orders.push({
            security: auxSecurity,
            transactionType: TransactionType.BUY,
            orderType: OrderType.CONDITIONAL_EXIT,
            brokerOrderType: Broker_OrderType.MARKET_ORDER,
            quantity: 1,
            isBasket: true,
            basketId: params.basketId,
            batchId: params.batchId,
            orderDate: Date.now(),
            product: Product.CNC,
            price: 0,
            triggerPrice: 0,
            brokerVariety: Broker_Variety.REGULAR,
            validity: Validity.DAY,
            clientOrderId: params.cxClientOrderId ?? uuid(),
            orderExecutionParams: exitParams,
            immediateExecutePaperOrder: false,
            ...(params.strategyGroup ? { strategyGroup: params.strategyGroup } : {}),
        })
    }
}
